import { Select, SelectProps, Spin, Tag } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { Project } from '../../model/entities';

/**
 * This component represents a project selector component.
 * @param props - the props
 */
const SelectProjectComponent: React.FC<Props> = (props) => {
    const { onList, onGet, providerTag, ...rest } = props;

    /** HOOKS */

    const [loading, setLoading] = useState<boolean>(false);
    const [projects, setProjects] = useState<Project[]>([]);
    const [searchText, setSearchText] = useState<string>();
    const [debouncedSearchText, setDebouncedSearchText] = useState<string>();
    const onListRef = useRef(onList);
    onListRef.current = onList;

    /** EFFECTS */

    // if the select already has a value on mount but no projects loaded yet, get the project or projects
    useEffect(() => {
        const init = async () => {
            if (rest.value && projects.length === 0) {
                // here, projectIds are always treated as an array, even if just one is received
                const projectIds = Array.isArray(rest.value) ? rest.value : [rest.value];
                const projectsFetched = await Promise.all(projectIds.map((id) => onGet(id)));
                // onGet can receive undefined because the api call can fail, so the list is filtered to keep just the ones fetched
                const projectsFiltered = projectsFetched.filter(Boolean) as Project[];
                if (projectsFiltered.length > 0) {
                    setProjects((prev) => [
                        ...projectsFiltered,
                        ...prev.filter((p) => projectsFiltered.every((v) => v.id !== p.id)),
                    ]);
                }
            }
        };
        init();
    }, [onGet, projects.length, rest.value]);

    // useEffect for debounce search projects for 1 second
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 1000);

        return () => clearTimeout(timer);
    }, [searchText]);

    // just call the api if the number of characters written is at least 2, else we clear the list
    useEffect(() => {
        const list = async () => {
            try {
                setLoading(true);
                if (debouncedSearchText && debouncedSearchText.length >= 2) {
                    const projects = await onListRef.current(debouncedSearchText);
                    setProjects(projects);
                } else {
                    setProjects([]);
                }
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        };
        list();
    }, [debouncedSearchText]);

    useEffect(() => {
        const resetDebouncedSearchtext = async () => {
            setDebouncedSearchText(undefined);
        };
        resetDebouncedSearchtext();
    }, [onList]);

    /** METHODS */

    /** VISUAL */

    // check if a value is received and there are not projects loaded and display an empty label on that case
    const projectOptions =
        rest.value && projects.length === 0
            ? (Array.isArray(rest.value) ? rest.value : [rest.value]).map((id) => (
                  <Select.Option key={id} value={id}>
                      {''}
                  </Select.Option>
              ))
            : projects.map((project) => (
                  <Select.Option key={project.id} value={project.id}>
                      {project.name}
                      {providerTag && project.platform && (
                          <>
                              {' '}
                              <Tag>{project.platform}</Tag>
                          </>
                      )}
                  </Select.Option>
              ));

    return (
        <Select
            filterOption={false}
            onSearch={setSearchText}
            notFoundContent={loading ? <Spin size="small" /> : undefined}
            {...rest}
        >
            {projectOptions}
        </Select>
    );
};
export default SelectProjectComponent;

interface CustomProps {
    onList: (searchText: string) => Promise<Project[]>;
    onGet: (id: string) => Promise<Project | undefined>;
    providerTag?: boolean;
}

type Props = CustomProps &
    Omit<SelectProps<string | string[]>, 'options' | 'onSearch' | 'notFoundContent' | 'onSelect'>;
